import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { CircularProgress, Box } from '@mui/material';
import { RootState, AppDispatch } from '../store';
import { getCurrentUser } from '../store/slices/authSlice';

/**
 * Auth Initializer Component
 * Checks for stored tokens on app load and restores authentication state.
 *
 * @author Civilhealth Development Team
 */

interface AuthInitializerProps {
  children: React.ReactNode;
}

const AuthInitializer: React.FC<AuthInitializerProps> = ({ children }) => {
  const dispatch = useDispatch<AppDispatch>();
  const { isAuthenticated } = useSelector((state: RootState) => state.auth);
  const [isInitialized, setIsInitialized] = React.useState(false);

  useEffect(() => {
    const initializeAuth = async () => {
      // Check if we have a stored token
      const storedToken = localStorage.getItem('accessToken');

      if (storedToken && !isAuthenticated) {
        // We have a token but not authenticated - try to restore session
        try {
          // Dispatch action to get current user using the stored token
          await dispatch(getCurrentUser() as any).unwrap();
        } catch (error) {
          // Token is invalid - clear storage silently
          console.log('Token validation failed, clearing auth');
          localStorage.removeItem('accessToken');
          localStorage.removeItem('refreshToken');
          localStorage.removeItem('user');
        }
      }

      // Always set initialized to true, even on error
      setIsInitialized(true);
    };

    // Set a timeout to ensure we don't get stuck on loading forever
    const timeoutId = setTimeout(() => {
      setIsInitialized(true);
    }, 5000);

    initializeAuth().finally(() => {
      clearTimeout(timeoutId);
    });

    return () => clearTimeout(timeoutId);
  }, [dispatch, isAuthenticated]);

  // Show loading state while initializing
  if (!isInitialized) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '100vh',
          bgcolor: 'transparent' ,
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return <>{children}</>;
};

export default AuthInitializer;